import { useAddMediaLinksToReport } from "@entities/reports/lib/hooks/useAddMediaLinksToReport";
import { useValidateReportCsv } from "@entities/reports/lib/hooks/useValidateReportCsv";
import { FirebaseService } from "@shared/lib/services/firebase.service";
import { ToastService } from "@shared/lib/services/toast.service";
import { Button } from "@shared/ui";
import { useEffect, useMemo, useState } from "react";
import {
  ACCEPTED_CSV_EXTENSIONS,
  REPORT_MEDIA_CSV_STORAGE_PATH,
} from "./lib/constants";
import styles from "./styles.module.scss";
import { ReviewStep } from "./ui/ReviewStep";
import { UploadStep } from "./ui/UploadStep";

type Step = "upload" | "review";

interface BulkAddReportMediaProps {
  reportId: string;
  onClose: () => void;
}

export function BulkAddReportMedia({
  reportId,
  onClose,
}: BulkAddReportMediaProps) {
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const {
    validateReportCsv,
    isValidating,
    isValidated,
    data: validateData,
  } = useValidateReportCsv();

  const { addMediaLinksToReport, isAdding, isAdded } =
    useAddMediaLinksToReport(reportId);

  const results = useMemo(() => validateData?.data ?? [], [validateData]);
  const successLinks = useMemo(
    () =>
      results
        .filter((r) => r.status === "success")
        .map((r) => r.link)
        .filter(Boolean),
    [results]
  );

  useEffect(() => {
    if (isValidated) {
      setStep("review");
    }
  }, [isValidated]);

  useEffect(() => {
    if (isAdded) {
      ToastService.success(
        `Added ${successLinks.length} link${
          successLinks.length === 1 ? "" : "s"
        } to report`
      );
      onClose();
    }
  }, [isAdded, onClose, successLinks.length]);

  const resetToUpload = () => {
    setFile(null);
    setStep("upload");
  };

  const handleFileSelect = async (selected: File) => {
    const ext = selected.name
      .toLowerCase()
      .substring(selected.name.lastIndexOf("."));
    if (!ACCEPTED_CSV_EXTENSIONS.includes(ext)) {
      ToastService.error("Please upload a .csv file");
      return;
    }

    setFile(selected);
    setIsUploading(true);
    try {
      const csvLink = await FirebaseService.uploadFileToFirebase(
        `${REPORT_MEDIA_CSV_STORAGE_PATH}/${reportId}`,
        selected
      );
      validateReportCsv(csvLink);
    } catch {
      ToastService.error("File upload failed. Please try again.");
      setFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleProceed = () => {
    if (successLinks.length === 0) return;
    addMediaLinksToReport({ reportingMedialinks: successLinks });
  };

  const isBusy = isUploading || isValidating;
  const hasAnySuccess = successLinks.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.body}>
        {step === "upload" ? (
          <UploadStep
            file={file}
            isBusy={isBusy}
            onFileSelect={handleFileSelect}
          />
        ) : (
          <ReviewStep fileName={file?.name || ""} results={results} />
        )}
      </div>

      <div className={styles.footer}>
        {step === "review" && (
          <Button
            type="text"
            onClick={resetToUpload}
            disabled={isAdding}
            className={styles.footerLeft}
          >
            Upload new file
          </Button>
        )}

        <Button onClick={onClose} disabled={isBusy || isAdding}>
          Cancel
        </Button>

        {step === "review" && (
          <Button
            type="primary"
            onClick={handleProceed}
            loading={isAdding}
            disabled={!hasAnySuccess || isAdding}
          >
            Proceed with {successLinks.length} link
            {successLinks.length === 1 ? "" : "s"}
          </Button>
        )}
      </div>
    </div>
  );
}
